<template>
    <div class="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm shadow-slate-200/50">
        <div :class="[bgColor, 'px-8 py-4']">
            <h3 class="font-black text-white text-lg">{{ title }} ({{ items.length }})</h3>
        </div>
        <div class="divide-y divide-slate-100 p-6">
            <div v-for="item in items" :key="item.id" class="py-6 first:pt-2 last:pb-2">
                <div class="flex items-start justify-between">
                    <div class="flex-1">
                        <div class="flex items-center gap-3 mb-3">
                            <h4 class="font-black text-slate-900 text-lg leading-tight uppercase tracking-tight">
                                {{ getNomenclatureName(item.nomenclatureId) }}
                            </h4>
                            <span class="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">
                                {{ item.quantity }} {{ getNomenclatureUnit(item.nomenclatureId) }}
                            </span>
                        </div>
                        
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                            <div>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.storageList.counterparty') }}</p>
                                <p class="text-sm font-bold text-slate-900">{{ getCounterpartyName(item.counterpartyId) }}</p>
                            </div>
                            <div>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.storageList.warehouse') }}</p>
                                <p class="text-sm font-bold text-slate-900">{{ getWarehouseName(item.warehouseId) }}</p>
                            </div>
                            <div>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.storageList.address') }}</p>
                                <p class="text-sm font-black text-slate-900 uppercase">
                                    {{ item.location.zone }}—{{ item.location.row }}—{{ item.location.rack }}—{{ item.location.level }}
                                </p>
                            </div>
                        </div>

                        <div class="mt-4 flex flex-wrap items-center gap-6">
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-full bg-slate-300"></div>
                                <p class="text-xs font-bold text-slate-500 uppercase tracking-tight">
                                    {{ $t('household.storageList.accepted') }} <span class="text-slate-900">{{ formatDate(item.startDate) }}</span>
                                </p>
                            </div>
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-full bg-slate-300"></div>
                                <p class="text-xs font-bold text-slate-500 uppercase tracking-tight">
                                    {{ $t('household.storageList.return') }} <span class="text-slate-900">{{ formatDate(item.endDate) }}</span>
                                </p>
                            </div>
                        </div>
                        
                        <p v-if="item.notes" class="mt-3 text-xs font-medium text-slate-400 italic">
                            &ldquo;{{ item.notes }}&rdquo;
                        </p>
                    </div>

                    <div class="text-right flex flex-col items-end gap-4 min-w-[140px]">
                        <template v-if="item.status === 'active'">
                            <div>
                                <p :class="['text-lg font-black leading-none mb-1', getDaysLeftClass(item.endDate)]">
                                    {{ getDaysLeftText(item.endDate) }}
                                </p>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ $t('household.storageList.tillEndPeriod') }}</p>
                            </div>
                            <button
                                @click="$emit('return', item.id)"
                                class="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl font-black text-[10px] tracking-widest uppercase hover:bg-blue-600 hover:text-white transition-all active:scale-95"
                            >
                                <LucideIcon :size="12">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                                </LucideIcon>
                                {{ $t('household.storageList.returnBtn') }}
                            </button>
                        </template>
                        <template v-else-if="item.status === 'returned'">
                            <span class="inline-flex items-center gap-2 bg-green-50 text-green-600 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest">
                                <LucideIcon :size="12">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                                </LucideIcon>
                                {{ $t('household.storageList.returnedTag') }}
                            </span>
                        </template>
                        <template v-else-if="item.status === 'expired'">
                            <span class="inline-flex items-center gap-2 bg-red-50 text-red-600 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest">
                                <LucideIcon :size="12">
                                    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                                </LucideIcon>
                                {{ $t('household.storageList.expiredTag') }}
                            </span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import LucideIcon from '@/components/common/LucideIcon.vue';

const { t } = useI18n();

const props = defineProps({
    items: Array,
    title: String,
    bgColor: String,
    nomenclatures: Array,
    warehouses: Array,
    counterparties: Array
});

defineEmits(['return']);

const now = new Date();

const getDaysLeft = (dateString) => {
    if (!dateString) return 0;
    return Math.floor((new Date(dateString).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const getDaysLeftText = (dateString) => {
    const days = getDaysLeft(dateString);
    return days < 0
        ? t('household.storageList.expiredDays', { days: Math.abs(days) })
        : t('household.storageList.daysLeft', { days });
};

const getDaysLeftClass = (dateString) => {
    const days = getDaysLeft(dateString);
    if (days < 0) return 'text-red-600';
    if (days <= 7) return 'text-orange-600';
    return 'text-green-600';
};

const getNomenclatureName = (id) => {
    return props.nomenclatures.find(n => n.id === id)?.name || t('household.shared.unknown');
};

const getNomenclatureUnit = (id) => {
    return props.nomenclatures.find(n => n.id === id)?.unit || '';
};

const getWarehouseName = (id) => {
    return props.warehouses.find(w => w.id === id)?.name || t('household.shared.unknown');
};

const getCounterpartyName = (id) => {
    return props.counterparties.find(c => c.id === id)?.name || t('household.shared.unknown');
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU');
};
</script>
